import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, Save, X, Loader2, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LANGS = [{ code: 'en', label: 'English' }, { code: 'hi', label: 'हिन्दी' }, { code: 'pa', label: 'ਪੰਜਾਬੀ' }];

const empty = () => ({
  name: { en: '', hi: '', pa: '' },
  location: { en: '', hi: '', pa: '' },
  role: { en: '', hi: '', pa: '' },
  quote: { en: '', hi: '', pa: '' },
});

const Input = ({ testId, value, onChange, type = 'input' }) => {
  const Cmp = type === 'textarea' ? 'textarea' : 'input';
  return (
    <Cmp
      data-testid={testId}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={type === 'textarea' ? 3 : undefined}
      className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-800/20"
      style={{ borderColor: '#E5E7EB' }}
    />
  );
};

export default function TestimonialsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });

  const fetchList = useCallback(async () => {
    setLoading(true);
    const res = await axios.get(`${BACKEND_URL}/api/testimonials`);
    setItems(res.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openEdit = (i) => { setEditing(JSON.parse(JSON.stringify(i))); setCreating(false); setActiveLang('en'); setStatus({ type: '', msg: '' }); };
  const openCreate = () => { setEditing(empty()); setCreating(true); setActiveLang('en'); setStatus({ type: '', msg: '' }); };
  const cancelEdit = () => { setEditing(null); setCreating(false); };
  const setLangField = (field, value) => setEditing((it) => ({ ...it, [field]: { ...it[field], [activeLang]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (creating) await axios.post(`${BACKEND_URL}/api/admin/testimonials`, editing, { withCredentials: true });
      else await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editing.id}`, editing, { withCredentials: true });
      await fetchList();
      setStatus({ type: 'success', msg: 'Saved' });
      setTimeout(() => { setEditing(null); setCreating(false); setStatus({ type: '', msg: '' }); }, 800);
    } catch (err) {
      setStatus({ type: 'error', msg: err?.response?.data?.detail || 'Save failed' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`, { withCredentials: true });
    fetchList();
  };

  if (loading) return <div className="py-12 text-center text-sm" style={{ color: '#6B7280' }}><Loader2 className="animate-spin inline mr-2" size={16} /> Loading...</div>;

  if (editing) {
    return (
      <div data-testid="testimonials-editor-form" className="max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg" style={{ color: '#0A3F22' }}>{creating ? 'New Testimonial' : `Edit: ${editing.name?.en}`}</h3>
          <button data-testid="testimonial-cancel-btn" onClick={cancelEdit} className="p-2 rounded-lg" style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}><X size={16} /></button>
        </div>
        <div className="flex gap-2 mb-3 border-b" style={{ borderColor: '#E5E7EB' }}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`testimonial-lang-${l.code}`}
              onClick={() => setActiveLang(l.code)}
              className="px-4 py-2 text-sm font-semibold border-b-2 -mb-px"
              style={{ borderColor: activeLang === l.code ? '#D97706' : 'transparent', color: activeLang === l.code ? '#0A3F22' : '#9CA3AF' }}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-xl p-6 border space-y-4" style={{ borderColor: '#E5E7EB' }}>
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>Name</label>
            <Input testId={`testimonial-name-${activeLang}`} value={editing.name?.[activeLang]} onChange={(v) => setLangField('name', v)} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>Location</label>
            <Input testId={`testimonial-location-${activeLang}`} value={editing.location?.[activeLang]} onChange={(v) => setLangField('location', v)} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>Role</label>
            <Input testId={`testimonial-role-${activeLang}`} value={editing.role?.[activeLang]} onChange={(v) => setLangField('role', v)} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>Quote</label>
            <Input testId={`testimonial-quote-${activeLang}`} type="textarea" value={editing.quote?.[activeLang]} onChange={(v) => setLangField('quote', v)} />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t mt-6 -mx-8 px-8 py-4 flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-2 text-sm">
            {status.type === 'success' && <span data-testid="testimonial-save-success" className="flex items-center gap-1.5" style={{ color: '#059669' }}><Check size={14} /> {status.msg}</span>}
            {status.type === 'error' && <span className="flex items-center gap-1.5" style={{ color: '#DC2626' }}><AlertCircle size={14} /> {status.msg}</span>}
          </div>
          <button data-testid="testimonial-save-btn" onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: '#0A3F22' }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="testimonials-editor" className="max-w-4xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-heading font-bold text-lg" style={{ color: '#0A3F22' }}>Testimonials ({items.length})</h3>
          <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>Customer quotes shown on the Home page.</p>
        </div>
        <button data-testid="testimonials-add-btn" onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90" style={{ backgroundColor: '#D97706' }}>
          <Plus size={14} /> Add Testimonial
        </button>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`testimonial-row-${it.id}`} className="bg-white rounded-xl p-4 border" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm" style={{ color: '#0A3F22' }}>{it.name?.en}</div>
                <div className="text-xs mt-0.5" style={{ color: '#4A554A' }}>{it.location?.en} · {it.role?.en}</div>
                <p className="text-sm mt-2 italic" style={{ color: '#4A554A' }}>&quot;{it.quote?.en}&quot;</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button data-testid={`testimonial-edit-${it.id}`} onClick={() => openEdit(it)} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold hover:opacity-80" style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}>
                  <Pencil size={12} /> Edit
                </button>
                <button data-testid={`testimonial-delete-${it.id}`} onClick={() => handleDelete(it.id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
